<template>
  <div class="page">
    <DowntimesToolbar
      :currentPage="filters.page"
      :totalPages="totalPages"
      :totalItems="totalItems"
      :refreshing="isFetching"
      :exporting="exporting"
      :filterOpen="showFilters"
      :sorting="filters.sorting"
      :columnOrder="tableState.columnOrder"
      :columnVisibility="tableState.columnVisibility"
      :scale="scale"
      :highlightMode="tableState.highlightMode"
      :palette="tableState.highlightPalette"
      @pageChanged="setPage"
      @refresh="refresh"
      @export="exportToExcel"
      @toggleFilter="showFilters = !showFilters"
      @columnOrderChanged="tableState.columnOrder = $event"
      @columnVisibilityChanged="tableState.columnVisibility = $event"
      @resetSettings="resetTableSettings"
      @scaleChanged="scale = $event"
      @highlightChanged="onHighlightChanged"
    />

    <FilterPresetBar
      :presets="presetPresets"
      :activeId="presetActiveId"
      :dirtyId="presetDirtyId"
      @apply="onPresetApply"
      @save="onPresetSave"
      @rename="onPresetRename"
      @remove="onPresetRemove"
      @reorder="onPresetReorder"
      @setDefault="onPresetSetDefault"
    />

    <div v-if="optionsError" class="options-warning" role="alert">
      Не удалось загрузить рабочие статусы рейсов.
      <button type="button" @click="retryOptions">Повторить</button>
    </div>

    <div class="page-layout" :class="{ 'has-filters': showFilters }">
      <div class="table-area">
        <div v-if="hasError && !items.length" class="error-block">
          Ошибка загрузки данных. Попробуйте обновить страницу.
        </div>
        <DowntimesTable
          v-else
          :items="items"
          :isLoading="loading"
          :isRefreshing="isFetching"
          :sorting="filters.sorting"
          :columnOrder="tableState.columnOrder"
          :columnVisibility="tableState.columnVisibility"
          :columnSizing="tableState.columnSizing"
          :currentPage="filters.page"
          :pageCount="totalPages"
          :workStatusOptions="workStatusOptions"
          :actingId="actingId"
          :downloadingActId="downloadingActId"
          :scale="scale"
          :highlightMode="tableState.highlightMode"
          :palette="tableState.highlightPalette"
          @sortingChanged="setSorting"
          @columnSizingChanged="tableState.columnSizing = $event"
          @navigate="navigateToTrip"
          @register="openAction('register', $event)"
          @resolve="openAction('resolve', $event)"
          @downloadAct="downloadAct"
        />
      </div>

      <aside v-if="showFilters" class="filters-sidebar">
        <DowntimesFilters :filters="filters" :workStatusOptions="workStatusOptions" :managerOptions="managerOptions" :optionsError="optionsError" @update="updateFilters" @reset="resetFilters" />
      </aside>
    </div>

    <DowntimeReasonDialog
      :open="Boolean(pendingAction)"
      :title="dialogTitle"
      :confirmLabel="pendingAction?.type === 'resolve' ? 'Закрыть простой' : 'Зарегистрировать'"
      :busy="Boolean(actingId)"
      @confirm="confirmAction"
      @cancel="closeDialog"
    />
  </div>
</template>

<script>
import { computed, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { createToaster } from '@meforma/vue-toaster';
import { downloadBlobResponse } from '@/backend/download';
import { exportDowntimes, fetchDowntimeAct, registerDowntime, resolveDowntime } from '@/backend/downtimesApi';
import { useUserTableScale } from '@/components/UI/useUserTableScale';
import { getBrowserStorage, normalizeTableScale } from '@/components/UI/tableScale.mjs';
import FilterPresetBar from '@/components/UI/FilterPresetBar.vue';
import { useFilterPresets } from '@/components/UI/useFilterPresets';
import DowntimesFilters from './components/DowntimesFilters.vue';
import DowntimesTable from './components/DowntimesTable.vue';
import DowntimesToolbar from './components/DowntimesToolbar.vue';
import DowntimeReasonDialog from './components/DowntimeReasonDialog.vue';
import { buildDowntimeParams, buildDowntimeReasonRequest, canDownloadDowntimeAct, canRegisterDowntime, canResolveDowntime, defaultTableState, loadTableState, materializeDatePeriod, normalizeTableState, saveTableState } from './downtimeState.mjs';
import { useDowntimes } from './useDowntimes';

export default {
  name: 'DowntimesPage',
  components: { DowntimesFilters, DowntimesTable, DowntimesToolbar, DowntimeReasonDialog, FilterPresetBar },
  setup() {
    const router = useRouter();
    const toaster = createToaster();
    const showFilters = ref(false);
    const exporting = ref(false);
    const actingId = ref(null);
    const downloadingActId = ref(null);
    const pendingAction = ref(null);
    const storage = getBrowserStorage();
    const tableState = reactive(loadTableState(storage));
    const { scale, resetScale } = useUserTableScale('downtimes');
    watch(tableState, (value) => saveTableState(storage, value), { deep: true });

    const downtimeState = useDowntimes();

    const captureView = () => {
      const f = downtimeState.filters;
      if (f.datePeriod) {
        const { page, plannedDateFrom, plannedDateTo, ...rest } = f;
        return { filters: rest, table: { ...tableState }, scale: scale.value };
      }
      const { page, ...rest } = f;
      return { filters: rest, table: { ...tableState }, scale: scale.value };
    };
    const applyStateOnly = (snap) => {
      if (!snap) return;
      if (snap.filters) {
        Object.assign(downtimeState.filters, snap.filters, { page: 1 });
        const materialized = materializeDatePeriod(downtimeState.filters);
        downtimeState.filters.plannedDateFrom = materialized.plannedDateFrom;
        downtimeState.filters.plannedDateTo = materialized.plannedDateTo;
      }
      if (snap.table) Object.assign(tableState, normalizeTableState(snap.table));
      if (snap.scale) scale.value = normalizeTableScale(snap.scale);
    };
    const applyView = (snap) => {
      applyStateOnly(snap);
      downtimeState.loadDowntimes().catch(() => {});
    };
    const presetCtrl = useFilterPresets({ storageKey: 'downtimesViewPresets', capture: captureView, apply: applyView });
    const initialDefault = presetCtrl.defaultPreset.value;
    if (initialDefault) {
      applyStateOnly(initialDefault.snapshot);
      presetCtrl.markApplied(initialDefault.id);
    }
    const onPresetApply = (id) => presetCtrl.applyPreset(id);
    const onPresetSave = (name) => {
      const res = presetCtrl.saveAs(name);
      if (!res?.ok) return;
      if (res.overwritten) toaster.info(`Пресет «${name}» перезаписан`);
      else toaster.success(`Пресет «${name}» сохранён`);
    };
    const onPresetRename = ({ id, name }) => {
      if (!presetCtrl.renamePreset(id, name)) toaster.error('Пресет с таким именем уже существует');
    };
    const onPresetRemove = (id) => presetCtrl.removePreset(id);
    const onPresetReorder = (idList) => presetCtrl.reorderPresets(idList);
    const onPresetSetDefault = (id) => presetCtrl.setDefaultPreset(id);

    const refresh = () => downtimeState.loadDowntimes().catch(() => toaster.error('Не удалось обновить данные'));
    const onHighlightChanged = (patch) => { Object.assign(tableState, patch) };
    const retryOptions = async () => {
      if (await downtimeState.loadOptions()) refresh();
    };
    const resetTableSettings = () => {
      Object.assign(tableState, defaultTableState());
      resetScale();
    };
    const navigateToTrip = (tripId) => {
      if (!tripId) return;
      router.push(`/trips/${tripId}`);
    };

    const exportToExcel = async () => {
      if (exporting.value) return;
      exporting.value = true;
      try {
        const response = await exportDowntimes(buildDowntimeParams(downtimeState.filters, { includePaging: false }));
        await downloadBlobResponse(response, 'downtimes.xlsx');
      } catch {
        toaster.error('Не удалось выгрузить данные в Excel');
      } finally {
        exporting.value = false;
      }
    };

    const openAction = (type, item) => {
      if (actingId.value) return;
      if (type === 'register' && !canRegisterDowntime(item)) return;
      if (type === 'resolve' && !canResolveDowntime(item)) return;
      pendingAction.value = { type, item };
    };
    const closeDialog = () => {
      if (!actingId.value) pendingAction.value = null;
    };
    const confirmAction = async (reason) => {
      const action = pendingAction.value;
      if (!action || actingId.value) return;
      actingId.value = action.item.route_point_id;
      try {
        const payload = buildDowntimeReasonRequest(reason);
        if (action.type === 'resolve') await resolveDowntime(action.item.route_point_id, payload);
        else await registerDowntime(action.item.route_point_id, payload);
        toaster.success(action.type === 'resolve' ? 'Простой закрыт' : 'Простой зарегистрирован');
        pendingAction.value = null;
        downtimeState.loadDowntimes().catch(() => {});
      } catch {
        toaster.error(action.type === 'resolve' ? 'Не удалось закрыть простой' : 'Не удалось зарегистрировать простой');
      } finally {
        actingId.value = null;
      }
    };

    const downloadAct = async (item) => {
      if (!canDownloadDowntimeAct(item) || downloadingActId.value) return;
      downloadingActId.value = item.route_point_id;
      try {
        const response = await fetchDowntimeAct(item.route_point_id);
        await downloadBlobResponse(response, `downtime_act_${item.trip_id || item.cargo_trip_id}_${item.route_point_id}.docx`);
        toaster.success('Акт простоя сформирован');
      } catch {
        toaster.error('Не удалось сформировать акт простоя');
      } finally {
        downloadingActId.value = null;
      }
    };

    return {
      ...downtimeState,
      showFilters,
      exporting,
      actingId,
      downloadingActId,
      pendingAction,
      tableState,
      scale,
      dialogTitle: computed(() => pendingAction.value?.type === 'resolve' ? 'Закрытие простоя' : 'Регистрация простоя'),
      refresh,
      onHighlightChanged,
      retryOptions,
      resetTableSettings,
      navigateToTrip,
      exportToExcel,
      openAction,
      closeDialog,
      confirmAction,
      downloadAct,
      presetPresets: presetCtrl.presets,
      presetActiveId: presetCtrl.activeId,
      presetDirtyId: presetCtrl.dirtyId,
      onPresetApply,
      onPresetSave,
      onPresetRename,
      onPresetRemove,
      onPresetReorder,
      onPresetSetDefault,
    };
  },
};
</script>

<style scoped>
.page { display: flex; flex-direction: column; height: 100%; min-height: 0; }
.page-layout { display: flex; flex: 1; min-height: 0; overflow: hidden; }
.table-area { flex: 1; min-width: 0; overflow: auto; padding: 0 12px 12px; }
.filters-sidebar { width: 300px; flex-shrink: 0; overflow-y: auto; border-left: 1px solid #e5e7eb; background: #fafafa; }
.error-block { margin-top: 12px; padding: 32px; border: 1px solid #fecaca; border-radius: 8px; background: #fef2f2; color: #b91c1c; text-align: center; }
.options-warning { margin: 8px 12px 0; padding: 8px 12px; border: 1px solid #fecdca; border-radius: 6px; background: #fef3f2; color: #b42318; font-size: 13px; }
.options-warning button { margin-left: 8px; padding: 2px 8px; border: 1px solid currentColor; border-radius: 4px; background: #fff; color: inherit; cursor: pointer; }
@media (max-width: 900px) {
  .page-layout.has-filters { flex-direction: column; overflow-y: auto; }
  .page-layout.has-filters .filters-sidebar { order: -1; width: auto; max-height: 46vh; overflow-y: auto; border-bottom: 1px solid #e5e7eb; border-left: 0; }
  .page-layout.has-filters .table-area { min-height: 280px; overflow: auto; }
}
</style>
